/**
 * A page body (a platform update, a page document) as static HTML.
 *
 * The read-only counterpart of the editor's renderers — apps/web's
 * DocumentView and apps/mobile's PageContentView — for places that can't run
 * either: a public web page, an RSS item, an email. Every block type in the
 * element catalog has a rule here; blocks that only mean something inside a
 * workspace (a live job list, an entity card, a link to an internal page) are
 * left out rather than rendered as dead references.
 *
 * Text blocks store HTML written in the editor. That HTML is the author's, and
 * this output can reach the open web, so it goes through an allow-list: the
 * inline marks the editor produces survive, everything else is reduced to its
 * text. Nothing here trusts the stored markup.
 *
 * The output uses `pb-*` class names (page block) and no inline styles, so
 * each host styles it its own way. Files are resolved by the caller — this
 * module has no idea how a deployment signs its URLs — through
 * `pageAttachmentIds` (what to fetch) and the `file` option (what came back).
 */
import { safeHref } from "./pageBlocks";

export interface PageHtmlFile {
  url: string;
  name?: string | null;
  mimeType?: string | null;
  size?: number | null;
  thumbnailUrl?: string | null;
}

export interface PageHtmlOptions {
  /** An attachment by id; a block whose file comes back empty is left out. */
  file?: (attachmentId: number) => PageHtmlFile | null | undefined;
}

type Block = {
  id?: string;
  typeId?: string;
  data?: Record<string, unknown>;
  elements?: unknown[];
  archived?: boolean;
};

const escapeHtml = (s: string): string =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const str = (v: unknown): string => (typeof v === "string" ? v : "");

const attachmentIdOf = (data: Record<string, unknown>): number | null => {
  const id = Number(data.attachmentId);
  return Number.isFinite(id) && id > 0 ? id : null;
};

// --- inline HTML -------------------------------------------------------------

/** Marks the editor writes, mapped to the tag the output uses for each. */
const INLINE_TAGS: Record<string, string> = {
  strong: "strong",
  b: "strong",
  em: "em",
  i: "em",
  u: "u",
  s: "s",
  strike: "s",
  del: "s",
  code: "code",
  sub: "sub",
  sup: "sup",
  mark: "mark",
};

/** Block-level wrappers inside a text block: dropped, their breaks kept. */
const BREAKING_TAGS = new Set(["p", "div", "li", "h1", "h2", "h3", "h4", "h5", "h6", "blockquote", "pre"]);

// A tag, a comment, or a run of text. Comments and anything tag-shaped that
// isn't a plain tag (a doctype, a CDATA section) are dropped whole.
// Attribute values are matched whole, quotes and all, so a `>` inside one
// doesn't end the tag early.
const TOKEN_RE = /<!--[\s\S]*?-->|<\/?([a-zA-Z][a-zA-Z0-9]*)\b((?:[^>"']|"[^"]*"|'[^']*')*)>|<[!?][^>]*>|[^<]+|</g;

/** Text between tags, made safe however it was (or wasn't) encoded. */
const safeText = (text: string): string =>
  text
    // An ampersand that doesn't start an entity is a literal one.
    .replace(/&(?!(?:[a-zA-Z][a-zA-Z0-9]{1,31}|#[0-9]{1,7}|#x[0-9a-fA-F]{1,6});)/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/** Script and style bodies are not text; drop them before tokenising. */
const stripCode = (html: string): string =>
  html.replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, "");

const hrefFrom = (attrs: string): string | null => {
  const m = attrs.match(/\bhref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i);
  if (!m) return null;
  const raw = (m[1] ?? m[2] ?? m[3] ?? "")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#0*39;|&apos;/g, "'");
  // Control characters and whitespace inside a scheme are how `java\tscript:`
  // slips past a prefix check; there is no legitimate use for them in an href.
  if (/[\u0000-\u001f\u007f]/.test(raw)) return null;
  return safeHref(raw);
};

/**
 * Editor HTML reduced to inline marks, links and line breaks.
 *
 * Every tag outside the allow-list goes, attributes and all; only `href`
 * survives, on links, and only with a scheme that navigates. Tags are
 * re-emitted from a stack, so the output is balanced whatever the input was.
 */
export function sanitizeInlineHtml(html: string): string {
  const out: string[] = [];
  const open: string[] = [];
  let pendingBreak = false;

  const emitBreak = () => {
    if (out.length) pendingBreak = true;
  };
  const flushBreak = () => {
    if (pendingBreak) out.push("<br>");
    pendingBreak = false;
  };

  for (const m of stripCode(html).matchAll(TOKEN_RE)) {
    const token = m[0];
    const name = m[1]?.toLowerCase();
    if (!name) {
      if (token.startsWith("<!--") || /^<[!?]/.test(token)) continue;
      flushBreak();
      out.push(safeText(token));
      continue;
    }
    const closing = token.startsWith("</");

    if (name === "br") {
      flushBreak();
      out.push("<br>");
      continue;
    }
    if (BREAKING_TAGS.has(name)) {
      if (closing) emitBreak();
      continue;
    }
    if (name === "script" || name === "style") {
      // stripCode removed the bodies of closed ones; an unclosed one is
      // dropped here and whatever followed it stays as escaped text.
      continue;
    }

    const tag = name === "a" ? "a" : INLINE_TAGS[name];
    if (!tag) continue;

    if (closing) {
      const at = open.lastIndexOf(tag);
      if (at === -1) continue;
      // Close everything opened inside it too, so nesting stays valid.
      while (open.length > at) out.push(`</${open.pop()}>`);
      continue;
    }

    flushBreak();
    if (tag === "a") {
      const href = hrefFrom(m[2] ?? "");
      if (!href) continue; // the text stays, unlinked
      const external = /^https?:/i.test(href);
      out.push(
        `<a href="${escapeHtml(href)}"${external ? ' target="_blank" rel="noopener noreferrer nofollow"' : ""}>`,
      );
    } else {
      out.push(`<${tag}>`);
    }
    open.push(tag);
  }
  while (open.length) out.push(`</${open.pop()}>`);
  return out.join("");
}

const inline = (html: string): string => sanitizeInlineHtml(html).trim();

// --- media -------------------------------------------------------------------

const YOUTUBE_RE = /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/i;
const VIMEO_RE = /vimeo\.com\/(?:video\/)?(\d{5,})/i;

export type LinkedVideo =
  | { kind: "embed"; src: string; allow: string; /** A still frame, where the host publishes one. */ poster?: string }
  | { kind: "file"; src: string };

/**
 * How to play a linked video: the privacy-preserving embed for YouTube and
 * Vimeo, the browser's own player for anything else. Null for a link that
 * isn't http(s).
 */
export function linkedVideo(url: string): LinkedVideo | null {
  const href = safeHref(url);
  if (!href || !/^https?:/i.test(href)) return null;
  const yt = href.match(YOUTUBE_RE);
  if (yt) {
    return {
      kind: "embed",
      src: `https://www.youtube-nocookie.com/embed/${yt[1]}`,
      allow: "accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen",
      poster: `https://img.youtube.com/vi/${yt[1]}/hqdefault.jpg`,
    };
  }
  const vimeo = href.match(VIMEO_RE);
  if (vimeo) {
    return { kind: "embed", src: `https://player.vimeo.com/video/${vimeo[1]}?dnt=1`, allow: "fullscreen; picture-in-picture" };
  }
  return { kind: "file", src: href };
}

/** A player for a linked video, as markup. */
function videoForUrl(url: string, caption: string): string | null {
  const video = linkedVideo(url);
  if (!video) return null;
  if (video.kind === "embed") {
    return `<iframe class="pb-embed" src="${escapeHtml(video.src)}" title="${escapeHtml(caption || "Video")}" loading="lazy" allow="${video.allow}" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>`;
  }
  return `<video class="pb-player" src="${escapeHtml(video.src)}" controls preload="metadata" playsinline></video>`;
}

const formatSize = (bytes: number | null | undefined): string => {
  if (!bytes || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// --- blocks ------------------------------------------------------------------

const LIST_TYPES: Record<string, { tag: "ul" | "ol"; className: string }> = {
  "page-bullet-list": { tag: "ul", className: "pb-list" },
  "page-numbered-list": { tag: "ol", className: "pb-list pb-list-numbered" },
  "page-todo-list": { tag: "ul", className: "pb-list pb-todo" },
  // An action item reads as a to-do; the item it links to is internal.
  "page-action-item": { tag: "ul", className: "pb-list pb-todo" },
};

/** Blocks that only mean something inside a workspace. */
const WORKSPACE_ONLY = /^(page-entity-|page-job-|page-link$|page-break$)/;

const indentOf = (block: Block): number => {
  const n = Number(block.data?.indent);
  return Number.isFinite(n) && n > 0 ? Math.min(8, Math.floor(n)) : 0;
};

function listItem(block: Block): string {
  const data = block.data ?? {};
  const text = inline(str(data.content)) || "&nbsp;";
  if (LIST_TYPES[block.typeId ?? ""]?.className.includes("pb-todo")) {
    const checked = data.checked === true;
    return `<li class="pb-todo-item${checked ? " is-checked" : ""}"><span class="pb-check" aria-hidden="true"></span><span class="pb-todo-text">${checked ? `<s>${text}</s>` : text}</span>`;
  }
  return `<li>${text}`;
}

/**
 * A run of consecutive list blocks of one kind, nested by their indent the way
 * the editor shows them: a deeper item opens a list inside the item above it.
 */
function renderList(run: Block[]): string {
  const kind = LIST_TYPES[run[0].typeId ?? ""];
  const out: string[] = [];
  const depthStack: number[] = [];
  const openList = () => out.push(`<${kind.tag} class="${kind.className}">`);
  const closeList = () => out.push(`</li></${kind.tag}>`);

  for (const block of run) {
    const depth = indentOf(block);
    if (!depthStack.length) {
      openList();
      depthStack.push(depth);
    } else if (depth > depthStack[depthStack.length - 1]) {
      openList();
      depthStack.push(depth);
    } else {
      while (depthStack.length > 1 && depth < depthStack[depthStack.length - 1]) {
        closeList();
        depthStack.pop();
      }
      out.push("</li>");
    }
    out.push(listItem(block));
  }
  while (depthStack.length) {
    closeList();
    depthStack.pop();
  }
  return out.join("");
}

function renderTable(data: Record<string, unknown>): string {
  const rowsIn = Array.isArray(data.rows) ? (data.rows as unknown[]) : [];
  const legacyColumns = Array.isArray(data.columns) ? (data.columns as unknown[]).map(str) : null;
  // Legacy tables kept their header names apart; they read as row 0.
  const rows = [...(legacyColumns ? [legacyColumns] : []), ...rowsIn.map((r) => (Array.isArray(r) ? r.map(str) : []))];
  if (!rows.length) return "";
  const headerRow = typeof data.headerRow === "boolean" ? data.headerRow : !!legacyColumns;
  const headerColumn = data.headerColumn === true;
  const body = rows
    .map((row, r) => {
      const cells = row
        .map((cell, c) => {
          const header = (headerRow && r === 0) || (headerColumn && c === 0);
          const tag = header ? "th" : "td";
          const scope = header ? (r === 0 && headerRow ? ' scope="col"' : ' scope="row"') : "";
          return `<${tag}${scope}>${escapeHtml(cell)}</${tag}>`;
        })
        .join("");
      return `<tr>${cells}</tr>`;
    })
    .join("");
  return `<div class="pb-table-wrap"><table class="pb-table"><tbody>${body}</tbody></table></div>`;
}

function renderBlock(block: Block, opts: PageHtmlOptions, renderChildren: (els: unknown[]) => string): string {
  const typeId = block.typeId ?? "";
  const data = block.data ?? {};
  const children = Array.isArray(block.elements) ? block.elements : [];
  const file = (): PageHtmlFile | null => {
    const id = attachmentIdOf(data);
    return id && opts.file ? opts.file(id) ?? null : null;
  };

  if (WORKSPACE_ONLY.test(typeId)) return "";

  const heading = /^page-heading-([1-4])$/.exec(typeId);
  if (heading) {
    const text = str(data.text).trim() ? escapeHtml(str(data.text).trim()) : inline(str(data.content));
    if (!text) return "";
    // The host page owns <h1> (the entry's title), so level 1 is an <h2>.
    const level = Number(heading[1]) + 1;
    return `<h${level} class="pb-heading pb-heading-${heading[1]}">${text}</h${level}>`;
  }

  switch (typeId) {
    case "page":
    case "pages":
      return renderChildren(children);

    case "page-text": {
      const text = inline(str(data.content));
      return text ? `<p class="pb-text">${text}</p>` : "";
    }

    case "page-quote": {
      const text = inline(str(data.content));
      return text ? `<blockquote class="pb-quote">${text}</blockquote>` : "";
    }

    case "page-code": {
      const code = str(data.content);
      if (!code.trim()) return "";
      // Code blocks store the code itself, not markup.
      return `<pre class="pb-code"><code>${escapeHtml(code)}</code></pre>`;
    }

    case "page-divider":
      return `<hr class="pb-divider">`;

    case "page-callout": {
      const inner = renderChildren(children);
      return inner
        ? `<aside class="pb-callout"><span class="pb-callout-icon" aria-hidden="true">💡</span><div class="pb-callout-body">${inner}</div></aside>`
        : "";
    }

    case "page-card": {
      const inner = renderChildren(children);
      return inner ? `<div class="pb-card">${inner}</div>` : "";
    }

    case "page-toggle": {
      const title = inline(str(data.content) || str(data.title)) || "Details";
      const open = data.collapsed === true ? "" : " open";
      return `<details class="pb-toggle"${open}><summary>${title}</summary><div class="pb-toggle-body">${renderChildren(children)}</div></details>`;
    }

    case "page-table":
      return renderTable(data);

    case "page-image": {
      const url = safeHref(str(data.imageUrl)) ?? file()?.url ?? null;
      if (!url) return "";
      const fit = str(data.fit) === "cover" ? " pb-image-cover" : "";
      return `<figure class="pb-image${fit}"><img src="${escapeHtml(url)}" alt="${escapeHtml(str(data.alt))}" loading="lazy" decoding="async"></figure>`;
    }

    case "page-video": {
      const caption = str(data.caption).trim();
      let player: string | null = null;
      if (str(data.videoUrl).trim()) {
        player = videoForUrl(str(data.videoUrl).trim(), caption);
      } else {
        const f = file();
        if (f?.url) {
          const poster = f.thumbnailUrl ? ` poster="${escapeHtml(f.thumbnailUrl)}"` : "";
          player = `<video class="pb-player" src="${escapeHtml(f.url)}"${poster} controls preload="metadata" playsinline></video>`;
        }
      }
      if (!player) return "";
      return `<figure class="pb-video">${player}${caption ? `<figcaption>${escapeHtml(caption)}</figcaption>` : ""}</figure>`;
    }

    case "page-audio": {
      const f = file();
      return f?.url ? `<audio class="pb-audio" src="${escapeHtml(f.url)}" controls preload="none"></audio>` : "";
    }

    case "page-file": {
      const f = file();
      if (!f?.url) return "";
      const name = f.name?.trim() || "Download";
      const size = formatSize(f.size);
      return `<a class="pb-file" href="${escapeHtml(f.url)}" target="_blank" rel="noopener noreferrer"><span class="pb-file-name">${escapeHtml(name)}</span>${size ? `<span class="pb-file-size">${size}</span>` : ""}</a>`;
    }

    case "page-location": {
      const address = str(data.address).trim();
      if (!address) return "";
      const maps = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
      return `<p class="pb-location"><a href="${escapeHtml(maps)}" target="_blank" rel="noopener noreferrer">${escapeHtml(address)}</a></p>`;
    }

    default: {
      // A block this renderer doesn't know yet: keep its prose if it has
      // any, so a new type degrades to text rather than a hole.
      const text = inline(str(data.content)) || escapeHtml(str(data.text).trim());
      const own = text ? `<p class="pb-text">${text}</p>` : "";
      return own + renderChildren(children);
    }
  }
}

/** The page body as HTML; empty when there is nothing to show. */
export function pageToHtml(content: unknown, opts: PageHtmlOptions = {}): string {
  const root = (content ?? {}) as { elements?: unknown };

  const renderChildren = (elements: unknown[]): string => {
    const blocks = (elements ?? []).filter(
      (raw): raw is Block => !!raw && typeof raw === "object" && !(raw as Block).archived,
    );
    const out: string[] = [];
    for (let i = 0; i < blocks.length; ) {
      const kind = blocks[i].typeId ?? "";
      if (LIST_TYPES[kind]) {
        // Consecutive items of one list kind are one list.
        const tag = LIST_TYPES[kind].className;
        let j = i;
        while (j < blocks.length && LIST_TYPES[blocks[j].typeId ?? ""]?.className === tag) j++;
        out.push(renderList(blocks.slice(i, j)));
        i = j;
        continue;
      }
      out.push(renderBlock(blocks[i], opts, renderChildren));
      i++;
    }
    return out.join("");
  };

  return renderChildren(Array.isArray(root.elements) ? root.elements : []);
}

/** The attachment ids a page body's media blocks refer to, for the caller to resolve. */
export function pageAttachmentIds(content: unknown): number[] {
  const ids = new Set<number>();
  const walk = (elements: unknown): void => {
    if (!Array.isArray(elements)) return;
    for (const raw of elements) {
      const block = (raw ?? {}) as Block;
      if (block.archived) continue;
      if (/^page-(image|video|audio|file)$/.test(block.typeId ?? "")) {
        const id = attachmentIdOf(block.data ?? {});
        if (id) ids.add(id);
      }
      walk(block.elements);
    }
  };
  walk((content as { elements?: unknown } | null)?.elements);
  return [...ids];
}
